/**
 * Generic Store
 *
 * Minimal reactive state container. State is immutable (frozen on every update).
 * Supports whole-state and selector-based subscriptions.
 *
 * @template T
 *
 * @example Basic usage
 *   import { Store } from '@theme/store';
 *
 *   const store = new Store({ count: 0 });
 *   store.subscribe((state) => console.log(state.count));
 *   store.setState({ count: 1 });
 *
 * @example Selector subscription (only fires when selected slice changes)
 *   store.subscribe(
 *     (s) => s.count,
 *     (count) => console.log('count changed:', count)
 *   );
 */

/**
 * Shallow equality check for two values.
 * Objects are compared key-by-key (one level deep).
 * @param {*} a
 * @param {*} b
 * @returns {boolean}
 */
function shallowEqual(a, b) {
	if (Object.is(a, b)) return true;
	if (typeof a !== 'object' || typeof b !== 'object') return false;
	if (a === null || b === null) return false;

	const keysA = Object.keys(a);
	const keysB = Object.keys(b);
	if (keysA.length !== keysB.length) return false;

	for (const key of keysA) {
		if (!Object.is(a[key], b[key])) return false;
	}
	return true;
}

export class Store {
	/** @type {Readonly<T>} */
	#state;

	/** @type {Set<{ selector: Function|null, listener: Function, lastSlice: * }>} */
	#subscribers = new Set();

	/** @type {boolean} */
	#debug;

	/**
	 * @param {T} initialState
	 * @param {{ debug?: boolean }} [options]
	 */
	constructor(initialState, { debug = false } = {}) {
		this.#state = Object.freeze({ ...initialState });
		this.#debug = debug;
	}

	/** @returns {Readonly<T>} */
	getState() {
		return this.#state;
	}

	/**
	 * Update state via partial object or updater function.
	 * Freezes the result and notifies subscribers.
	 *
	 * @param {Partial<T> | ((prev: Readonly<T>) => Partial<T>)} partialOrUpdater
	 */
	setState(partialOrUpdater) {
		const prev = this.#state;
		const partial = typeof partialOrUpdater === 'function'
			? partialOrUpdater(prev)
			: partialOrUpdater;

		const next = { ...prev, ...partial };

		// Bail if nothing actually changed — avoids unnecessary freeze + notifications
		if (shallowEqual(prev, next)) return;

		this.#state = Object.freeze(next);

		if (this.#debug) {
			console.debug('[Store] state updated', { prev, next: this.#state });
		}

		this.#notify(prev);
	}

	/**
	 * Subscribe to state changes.
	 *
	 * - `subscribe(listener)` — called on every state change
	 * - `subscribe(selector, listener)` — called only when the selected slice changes
	 *
	 * @param {Function} selectorOrListener
	 * @param {Function} [listener]
	 * @returns {() => void} Unsubscribe function
	 */
	subscribe(selectorOrListener, listener) {
		/** @type {{ selector: Function|null, listener: Function, lastSlice: * }} */
		const descriptor = listener
			? { selector: selectorOrListener, listener, lastSlice: selectorOrListener(this.#state) }
			: { selector: null, listener: selectorOrListener, lastSlice: undefined };

		this.#subscribers.add(descriptor);

		return () => {
			this.#subscribers.delete(descriptor);
		};
	}

	/**
	 * Notify subscribers. Iterates a snapshot of the set so subscribers
	 * can safely unsubscribe during notification.
	 * @param {Readonly<T>} prev
	 */
	#notify(prev) {
		for (const descriptor of [...this.#subscribers]) {
			try {
				const { selector, listener, lastSlice } = descriptor;

				if (selector) {
					const nextSlice = selector(this.#state);
					if (!shallowEqual(nextSlice, lastSlice)) {
						descriptor.lastSlice = nextSlice;
						listener(nextSlice, lastSlice);
					}
				} else {
					listener(this.#state, prev);
				}
			} catch (error) {
				console.error('[Store] subscriber threw during notification', error);
			}
		}
	}
}
